// Topbar daemon-health widget — the React port of layout.php's
// render_poller_widget_body: three little Status / Routers / OLTs
// readouts (coloured dot + live counts), backed by GET /v1/api/dashboard/
// poller and kept fresh on every routers/olts SSE event (60s safety net).
// Plus RestartDaemonButton — the admin-only "Restart" control that sits
// next to it in the navbar (port of layout.php's #topPollerRestart).

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { RotateCw, AlertTriangle } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  cn,
  toast,
} from "@noc/ui";
import { api } from "@/lib/api";
import { useRole } from "@/features/auth/roles";
import { usePollerWidget } from "@/features/fleet/useFleet";
import type { FleetCount } from "@noc/api";

function statusTone(status: string): { dot: string; text: string } {
  switch (status) {
    case "Offline":
      return { dot: "bg-destructive", text: "text-destructive" };
    case "Degraded":
      return { dot: "bg-warning", text: "text-warning" };
    case "Operational":
      return { dot: "bg-success", text: "text-success" };
    default:
      return { dot: "bg-muted-foreground/40", text: "text-muted-foreground" };
  }
}

function countTone(down: number): string {
  if (down === 0) return "text-success";
  if (down === 1) return "text-warning";
  return "text-destructive";
}

function beatTitle(alive: boolean, ageS: number): string {
  if (!alive) return "No heartbeat for 2 min+";
  if (ageS < 60) return `Heartbeat ${ageS}s ago`;
  return `Heartbeat ${Math.floor(ageS / 60)}m ${ageS % 60}s ago`;
}

// Daemon-health is a NOC-operator readout: a reseller-role login doesn't
// get /v1/api/dashboard/poller (or /stream) — so render nothing for them
// rather than show three dashes and 403 on every page. (useRole() is a
// hook, called unconditionally; the body's data hooks only mount when
// PollerBody renders.)
export function PollerWidget() {
  return useRole() === "reseller" ? null : <PollerBody />;
}

function PollerBody() {
  const { data } = usePollerWidget();
  const p = data?.ok ? data.poller : undefined;
  const status = p?.status ?? "—";
  const tone = statusTone(status);

  return (
    <div
      className="hidden items-center gap-3 text-xs lg:flex"
      title={p ? beatTitle(p.alive, p.beat_age_s) : "loading…"}
    >
      <Col label="Status">
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <span className={cn("inline-block size-2 rounded-full", tone.dot)} />
          <span className={tone.text}>{status}</span>
        </span>
      </Col>
      <Divider />
      <Col label="Routers">
        <FleetNum c={p?.routers} />
      </Col>
      <Divider />
      <Col label="OLTs">
        <FleetNum c={p?.olts} />
      </Col>
    </div>
  );
}

function Col({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="leading-tight">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-medium">{children}</div>
    </div>
  );
}

function Divider() {
  return <span className="h-7 w-px bg-border" />;
}

function FleetNum({ c }: { c?: FleetCount }) {
  if (!c) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="tabular-nums">
      <span className={countTone(c.down)}>{c.online}</span>
      <span className="text-muted-foreground">/{c.total}</span>
    </span>
  );
}

// Admin-only "Restart" button next to the poller widget — POSTs
// /v1/api/cmd/restart, which acks immediately then exits ~50ms later;
// systemd (Restart=always) brings it back in ~1s. The SSE stream drops
// and reconnects on its own, so we just keep the button in a "Restarting"
// state for a few seconds and let the widget recover. Renders nothing for
// non-admins (the server rejects them anyway — this just hides the UI).
export function RestartDaemonButton() {
  const role = useRole();
  const [open, setOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const m = useMutation({
    mutationFn: () => api.cmd.restart(),
    onSuccess: (r) => {
      setOpen(false);
      if (r.ok) {
        setRestarting(true);
        toast.success("noc-app is restarting…");
        setTimeout(() => setRestarting(false), 7000);
      } else {
        toast.error(r.error ?? "restart failed");
      }
    },
    onError: (e) => {
      setOpen(false);
      toast.error(e instanceof Error ? e.message : "restart failed");
    },
  });

  if (role !== "admin") return null;
  const busy = m.isPending || restarting;

  return (
    <>
      <span className="mx-1 hidden h-7 w-px bg-border lg:block" />
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => setOpen(true)}
        title="Restart noc-app"
        className="hidden border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground lg:inline-flex"
      >
        {busy ? <Spinner /> : <RotateCw />}
        {busy ? "Restarting" : "Restart"}
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && !m.isPending && setOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-destructive" />
              Restart noc-app?
            </DialogTitle>
            <DialogDescription>
              The application daemon restarts in ~1 second (cmd-api + SSE + jobs). Device connections in noc-link are not
              affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={m.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => m.mutate()} disabled={m.isPending}>
              {m.isPending ? <Spinner /> : <RotateCw />}
              Restart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
